import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PdfViewer from "@/components/PdfViewer";
import AuthImage from "@/components/AuthImage";
import {
  FileText, Image as ImageIcon, Search, Eye, Download, Trash2,
  ShieldCheck, AlertTriangle, Building2, FolderOpen,
} from "lucide-react";
import { formatDate } from "@/lib/dict";
import { toast } from "sonner";

const MANAGER_ROLES = ["super_admin", "org_admin", "property_manager", "asset_manager", "technical_manager"];

const CONTEXT_META = {
  asset: { label: "Actif", icon: Building2, to: (d) => `/assets/${d.context_id}` },
  defect: { label: "Désordre", icon: AlertTriangle, to: () => "/defects" },
  claim: { label: "Sinistre", icon: ShieldCheck, to: () => "/claims" },
  certificate: { label: "Attestation", icon: FileText, to: () => "/insurance" },
};

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

async function downloadFile(url, filename) {
  try {
    const token = localStorage.getItem("wrm_token");
    const fullUrl = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
    const res = await fetch(fullUrl, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error();
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(objUrl);
  } catch {
    toast.error("Échec du téléchargement");
  }
}

export default function Documents() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [contextFilter, setContextFilter] = useState("all");
  const [preview, setPreview] = useState(null);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["documents"],
    queryFn: () => api.get("/documents").then((r) => r.data),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (q && !d.name.toLowerCase().includes(q)) return false;
      if (category !== "all" && d.category !== category) return false;
      if (contextFilter !== "all" && d.context_type !== contextFilter) return false;
      return true;
    });
  }, [docs, search, category, contextFilter]);

  const canDelete = (d) => MANAGER_ROLES.includes(user?.role) || d.uploaded_by === user?.id;

  const handleDelete = async (d) => {
    if (!window.confirm(`Supprimer « ${d.name} » ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      qc.invalidateQueries({ queryKey: ["documents"] });
      toast.success("Document supprimé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la suppression");
    }
  };

  return (
    <div className="space-y-5" data-testid="documents-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Module</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1">Documents</h1>
        <p className="text-sm text-slate-500 mt-1.5">{filtered.length} / {docs.length} document(s)</p>
      </div>

      <div className="flex flex-wrap gap-2.5">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="documents-search"
            placeholder="Rechercher un document..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="documents-filter-category" className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            <SelectItem value="photo">Photos</SelectItem>
            <SelectItem value="document">Documents</SelectItem>
          </SelectContent>
        </Select>
        <Select value={contextFilter} onValueChange={setContextFilter}>
          <SelectTrigger data-testid="documents-filter-context" className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les origines</SelectItem>
            <SelectItem value="asset">Actifs</SelectItem>
            <SelectItem value="defect">Désordres</SelectItem>
            <SelectItem value="claim">Sinistres</SelectItem>
            <SelectItem value="certificate">Attestations</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200">
        {isLoading ? (
          <div className="text-sm text-slate-400 text-center py-12">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div data-testid="documents-empty" className="text-sm text-slate-400 text-center py-12 flex flex-col items-center gap-2">
            <FolderOpen size={24} className="text-slate-300" />
            {docs.length === 0
              ? "Aucun document encore téléversé. Téléversez des photos et rapports depuis les désordres, sinistres et attestations."
              : "Aucun document ne correspond à votre recherche."}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((d) => {
              const isImg = d.category === "photo" || (d.mime_type || "").startsWith("image");
              const ctx = CONTEXT_META[d.context_type];
              return (
                <div key={d.id} data-testid={`document-row-${d.id}`} className="p-3 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                  {isImg ? (
                    <AuthImage
                      src={d.url} alt={d.name}
                      className="w-10 h-10 rounded-md object-cover border border-slate-200 flex-shrink-0"
                      fallback={<div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0"><ImageIcon size={16} className="text-slate-400" /></div>}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0"><FileText size={16} className="text-slate-400" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{d.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap mt-0.5">
                      <span>{formatDate(d.created_at)}</span>
                      {d.size ? <span>• {formatSize(d.size)}</span> : null}
                      {d.uploaded_by_name && <span>• {d.uploaded_by_name}</span>}
                      {ctx && (
                        <Link
                          to={ctx.to(d)}
                          data-testid={`document-context-${d.id}`}
                          className="inline-flex items-center gap-1 text-[#023E73] font-medium hover:underline"
                        >
                          • <ctx.icon size={11} /> {d.context_label || ctx.label}{d.context_asset_name ? ` — ${d.context_asset_name}` : ""}
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button data-testid={`document-view-${d.id}`} variant="ghost" size="sm" onClick={() => setPreview(d)} title="Voir">
                      <Eye size={15} />
                    </Button>
                    <Button data-testid={`document-download-${d.id}`} variant="ghost" size="sm" onClick={() => downloadFile(d.url, d.name)} title="Télécharger">
                      <Download size={15} />
                    </Button>
                    {canDelete(d) && (
                      <Button
                        data-testid={`document-delete-${d.id}`} variant="ghost" size="sm"
                        onClick={() => handleDelete(d)} title="Supprimer"
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 size={15} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="truncate pr-6">{preview?.name}</DialogTitle>
            <DialogDescription className="sr-only">Aperçu du document {preview?.name}</DialogDescription>
          </DialogHeader>
          {preview?.url && <PdfViewer src={preview.url} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
